import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Truck, Send, PackageCheck, PackagePlus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmtErr } from "../../lib/api";

const PO_STATUS = {
  draft: "bg-zinc-400/10 text-zinc-300 border-zinc-400/30",
  sent: "bg-blue-400/10 text-blue-300 border-blue-400/30",
  received: "bg-emerald-400/10 text-emerald-300 border-emerald-400/30",
};

export default function SuppliersTab({ restaurant }) {
  const [suppliers, setSuppliers] = useState([]);
  const [pos, setPos] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [supForm, setSupForm] = useState({ name: "", email: "", phone: "" });
  const [poForm, setPoForm] = useState(null);

  const load = () => {
    api.get(`/restaurants/${restaurant.id}/suppliers`).then((r) => setSuppliers(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/purchase-orders`).then((r) => setPos(r.data)).catch(() => {});
    api.get(`/restaurants/${restaurant.id}/ingredients`).then((r) => setIngredients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [restaurant.id]);

  const addSupplier = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/suppliers`, supForm);
      toast.success("Supplier added");
      setSupForm({ name: "", email: "", phone: "" });
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeSupplier = async (sid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/suppliers/${sid}`);
      toast.success("Supplier removed");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const createPo = async () => {
    try {
      await api.post(`/restaurants/${restaurant.id}/purchase-orders`, {
        supplier_id: poForm.supplier_id, notes: poForm.notes,
        items: poForm.items.filter((i) => i.ingredient_id && Number(i.qty) > 0)
          .map((i) => ({ ingredient_id: i.ingredient_id, qty: Number(i.qty) })),
      });
      toast.success("Purchase order created");
      setPoForm(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const poFromAlerts = async (sid) => {
    try {
      const { data } = await api.post(`/restaurants/${restaurant.id}/purchase-orders/from-alerts`, { supplier_id: sid });
      if (data.status === "empty") toast.info(data.message);
      else toast.success("Draft PO created from low-stock alerts");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const deletePo = async (poid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/purchase-orders/${poid}`);
      toast.success("Purchase order deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const poAction = async (po, action) => {
    try {
      await api.post(`/restaurants/${restaurant.id}/purchase-orders/${po.id}/${action}`);
      toast.success(action === "send" ? "PO emailed to supplier" : "Stock received into inventory");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div data-testid="suppliers-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
            <Truck className="w-5 h-5 text-primary" /> Suppliers
          </h3>
          <div className="mt-4 space-y-3">
            {suppliers.map((s) => (
              <div key={s.id} data-testid={`supplier-row-${s.id}`} className="rounded-xl border border-border/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{s.name}</p>
                    <p className="text-xs text-muted-foreground">{[s.email, s.phone].filter(Boolean).join(" · ") || "No contact details"}</p>
                  </div>
                  <button data-testid={`delete-supplier-${s.id}`} onClick={() => removeSupplier(s.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Button data-testid={`po-from-alerts-${s.id}`} size="sm" variant="outline" className="rounded-full h-8 mt-3"
                  onClick={() => poFromAlerts(s.id)}>
                  <PackagePlus className="w-3.5 h-3.5 mr-1.5" /> Draft PO from low stock
                </Button>
              </div>
            ))}
            {suppliers.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="no-suppliers-msg">No suppliers yet.</p>
            )}
          </div>
          <div className="mt-5 space-y-3 border-t border-border/40 pt-5">
            <Input data-testid="supplier-name-input" placeholder="Supplier name" value={supForm.name}
              onChange={(e) => setSupForm({ ...supForm, name: e.target.value })} className="bg-background border-border/60" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="supplier-email-input" type="email" placeholder="Order email" value={supForm.email}
                onChange={(e) => setSupForm({ ...supForm, email: e.target.value })} className="bg-background border-border/60" />
              <Input data-testid="supplier-phone-input" placeholder="Phone" value={supForm.phone}
                onChange={(e) => setSupForm({ ...supForm, phone: e.target.value })} className="bg-background border-border/60" />
            </div>
            <Button data-testid="add-supplier-btn" onClick={addSupplier} disabled={!supForm.name} className="rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add supplier
            </Button>
          </div>
        </div>

        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-outfit font-medium text-lg">Purchase orders</h3>
            <Button data-testid="new-po-btn" size="sm" className="rounded-full" disabled={suppliers.length === 0 || ingredients.length === 0}
              onClick={() => setPoForm({ supplier_id: suppliers[0]?.id || "", notes: "", items: [{ ingredient_id: "", qty: "" }] })}>
              <Plus className="w-4 h-4 mr-2" /> New PO
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {pos.map((po) => (
              <div key={po.id} data-testid={`po-row-${po.id}`} className="rounded-xl border border-border/40 p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium">{po.supplier_name} <span className="text-muted-foreground font-normal">· £{Number(po.total).toFixed(2)}</span></p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {po.items.map((i) => `${i.qty}${i.unit} ${i.name}`).join(", ")}
                    </p>
                  </div>
                  <Badge variant="outline" className={PO_STATUS[po.status] || PO_STATUS.draft}>{po.status}</Badge>
                </div>
                <div className="flex gap-2 mt-3">
                  {po.status === "draft" && (
                    <Button data-testid={`po-send-${po.id}`} size="sm" variant="outline" className="rounded-full h-8"
                      onClick={() => poAction(po, "send")}>
                      <Send className="w-3.5 h-3.5 mr-1.5" /> Email to supplier
                    </Button>
                  )}
                  {po.status !== "received" && (
                    <Button data-testid={`po-receive-${po.id}`} size="sm" variant="outline" className="rounded-full h-8"
                      onClick={() => poAction(po, "receive")}>
                      <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Mark received
                    </Button>
                  )}
                  {po.status !== "received" && (
                    <Button data-testid={`po-delete-${po.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                      onClick={() => deletePo(po.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {pos.length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="no-pos-msg">
                No purchase orders yet — create one manually or draft one from low-stock alerts.
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!poForm} onOpenChange={() => setPoForm(null)}>
        <DialogContent className="bg-card border-border/60 max-h-[85vh] overflow-y-auto">
          {poForm && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">New purchase order</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2" data-testid="po-editor">
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Supplier</Label>
                  <Select value={poForm.supplier_id} onValueChange={(v) => setPoForm({ ...poForm, supplier_id: v })}>
                    <SelectTrigger data-testid="po-supplier-select" className="mt-1.5 bg-background border-border/60">
                      <SelectValue placeholder="Choose supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {poForm.items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <Select value={item.ingredient_id}
                      onValueChange={(v) => setPoForm({ ...poForm, items: poForm.items.map((it, j) => (j === i ? { ...it, ingredient_id: v } : it)) })}>
                      <SelectTrigger data-testid={`po-ingredient-${i}`} className="bg-background border-border/60 col-span-7 h-9 text-sm">
                        <SelectValue placeholder="Ingredient" />
                      </SelectTrigger>
                      <SelectContent>
                        {ingredients.map((ing) => (
                          <SelectItem key={ing.id} value={ing.id}>{ing.name} (stock {ing.stock_level}{ing.unit})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input data-testid={`po-qty-${i}`} type="number" min="0" placeholder="Qty" value={item.qty}
                      onChange={(e) => setPoForm({ ...poForm, items: poForm.items.map((it, j) => (j === i ? { ...it, qty: e.target.value } : it)) })}
                      className="bg-background border-border/60 col-span-3 h-9 text-sm" />
                    <button data-testid={`po-remove-item-${i}`}
                      onClick={() => setPoForm({ ...poForm, items: poForm.items.filter((_, j) => j !== i) })}
                      className="text-muted-foreground hover:text-destructive transition-colors col-span-2 flex justify-center">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button data-testid="po-add-item" size="sm" variant="outline" className="rounded-full h-8"
                  onClick={() => setPoForm({ ...poForm, items: [...poForm.items, { ingredient_id: "", qty: "" }] })}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                </Button>
                <Textarea data-testid="po-notes-input" rows={2} placeholder="Notes for the supplier (optional)" value={poForm.notes}
                  onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} className="bg-background border-border/60" />
                <Button data-testid="po-create-btn" onClick={createPo}
                  disabled={!poForm.supplier_id || !poForm.items.some((i) => i.ingredient_id && Number(i.qty) > 0)}
                  className="w-full rounded-full">
                  Create purchase order
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
